'use client';

import React, { useState } from 'react';


// ─── WIDGET TEST HARNESS ───────────────────────────────────────────────────────
// Shows each widget (MRG, IRL, Names, Glossary, Keys, Asbab Quest)
// embedded on 5 platform templates

const WIDGETS = [
  {
    id: 'mrg',
    label: 'Muslim Resource Guide',
    arabic: 'دَلِيل الْمَوَارِد',
    icon: '⬡',
    color: '#2d7a47',
    desc: 'City/ward resource guide with Names of Allah, Ayat mosaic, disparities, and context.',
    embedUrl: 'https://haalproject.org/mrg-embed',
    sizes: ['1×1', '2×1', '4×2'],
  },
  {
    id: 'irl',
    label: 'IRL Lens',
    arabic: 'عَدَسَة الْوَاقِع',
    icon: '◎',
    color: '#2d8a87',
    desc: '8-layer Inner Reality Lens walkthrough — Zahir → Sunnat Allah → Haqq → Amanah → Uboor.',
    embedUrl: 'https://haalproject.org/inner-reality-lens',
    sizes: ['2×1', '4×2'],
  },
  {
    id: 'names',
    label: 'Names of Allah',
    arabic: 'أَسْمَاء اللَّه',
    icon: '✦',
    color: '#8a8ab8',
    desc: 'Names reference with root analysis, Quranic ayat, and community connections.',
    embedUrl: 'https://haalproject.org/names-reference',
    sizes: ['1×1', '2×1', '4×2'],
  },
  {
    id: 'glossary',
    label: 'SL Glossary',
    arabic: 'الْمُعْجَم',
    icon: '◈',
    color: '#c9a84c',
    desc: 'Spiritual Language glossary — key terms, root analysis, and platform connections.',
    embedUrl: 'https://haalproject.org/sl-glossary',
    sizes: ['1×1', '2×1'],
  },
  {
    id: 'keys',
    label: 'Keys to Kulli Haal',
    arabic: 'مَفَاتِيح كُلِّي حَال',
    icon: '⌘',
    color: '#D4A82A',
    desc: 'The 7 Keys curriculum — Ayat, Fitrah, Aql, Ilm, Amanah, Islah, Uboor.',
    embedUrl: 'https://haalproject.org/keys-to-kulli-haal',
    sizes: ['2×1', '4×2'],
  },
  {
    id: 'asbab',
    label: 'Asbab Quest',
    arabic: 'قُوَّة الأَسْبَاب',
    icon: '⊕',
    color: '#e07a2a',
    desc: 'Bridged community quests linking IRL observations to MRG resources and Islah actions.',
    embedUrl: 'https://haalproject.org/asbab-quest',
    sizes: ['2×1', '4×2'],
  },
];

const PLATFORMS = [
  {
    id: 'masjid',
    label: 'Masjid Site',
    arabic: 'مَوْقِع الْمَسْجِد',
    icon: '🕌',
    color: '#2d7a47',
    desc: 'A masjid website embedding the MRG for their city, Names reference, and Keys curriculum on their learning page.',
    bg: '#0a1a0f',
    accent: '#2d7a47',
    suggestedWidgets: ['mrg', 'names', 'keys'],
    mockUrl: 'masjid-al-faatir.org/resources',
    mockTitle: 'Masjid Al-Faatir · Community Resources',
  },
  {
    id: 'community-org',
    label: 'Community Org',
    arabic: 'مُنَظَّمَة مُجْتَمَعِيَّة',
    icon: '🏘',
    color: '#2d8a87',
    desc: 'A community organization embedding the MRG for their ward and Asbab Quest for volunteer coordination.',
    bg: '#0a1518',
    accent: '#2d8a87',
    suggestedWidgets: ['mrg', 'asbab', 'irl'],
    mockUrl: 'iman-chicago.org/ward-resources',
    mockTitle: 'IMAN Chicago · Ward Resource Hub',
  },
  {
    id: 'activism',
    label: 'Activism Hub',
    arabic: 'مَرْكَز النَّشَاط',
    icon: '✊',
    color: '#c9a84c',
    desc: 'An activism hub embedding the IRL Lens walkthrough and Asbab Quest for observation-to-action chains.',
    bg: '#181208',
    accent: '#c9a84c',
    suggestedWidgets: ['irl', 'asbab', 'mrg'],
    mockUrl: 'justice-collective.org/tools',
    mockTitle: 'Justice Collective · Observation Tools',
  },
  {
    id: 'educational',
    label: 'Educational Site',
    arabic: 'مَوْقِع تَعْلِيمِيّ',
    icon: '📖',
    color: '#8a8ab8',
    desc: 'An Islamic school or educational platform embedding Names reference, SL Glossary, and Keys curriculum.',
    bg: '#0e0e18',
    accent: '#8a8ab8',
    suggestedWidgets: ['names', 'glossary', 'keys'],
    mockUrl: 'al-ilm-academy.org/curriculum',
    mockTitle: 'Al-Ilm Academy · Curriculum Tools',
  },
  {
    id: 'standalone',
    label: 'Standalone',
    arabic: 'مُسْتَقِل',
    icon: '◎',
    color: '#e07a2a',
    desc: 'A standalone embed — a single widget deployed directly to any web page with no surrounding platform context.',
    bg: '#0d0a06',
    accent: '#e07a2a',
    suggestedWidgets: ['mrg', 'irl', 'asbab'],
    mockUrl: 'haalproject.org/mrg-embed?city=chicago&ward=englewood',
    mockTitle: 'Standalone MRG Embed · Englewood',
  },
];

const C = {
  bg: '#060d12',
  panel: 'rgba(255,255,255,0.02)',
  border: 'rgba(201,168,76,0.12)',
  gold: '#c9a84c',
  goldText: 'rgba(201,168,76,0.6)',
  cream: '#F5F0E8',
  creamFaint: 'rgba(212,197,169,0.5)',
};

// ── Widget Preview Card ───────────────────────────────────────────────────────
function WidgetCard({ widget, selected, onClick }: { widget: typeof WIDGETS[0]; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{ padding: '12px 14px', background: selected ? `${widget.color}12` : 'rgba(255,255,255,0.02)', border: `1px solid ${selected ? widget.color + '40' : 'rgba(201,168,76,0.1)'}`, borderRadius: 6, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 16, color: widget.color }}>{widget.icon}</span>
        <div>
          <p style={{ fontFamily: 'monospace', fontSize: 9, color: selected ? widget.color : C.cream, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 700 }}>{widget.label}</p>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 10, color: `${widget.color}80`, direction: 'rtl' }}>{widget.arabic}</p>
        </div>
      </div>
      <p style={{ color: C.creamFaint, fontSize: 9, lineHeight: 1.5, marginBottom: 6 }}>{widget.desc}</p>
      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {widget.sizes.map(s => (
          <span key={s} style={{ fontFamily: 'monospace', fontSize: 6, color: widget.color, background: `${widget.color}10`, border: `1px solid ${widget.color}20`, padding: '1px 5px', borderRadius: 2 }}>{s}</span>
        ))}
      </div>
    </button>
  );
}

// ── Platform Template Preview ─────────────────────────────────────────────────
function PlatformPreview({ platform, activeWidget }: { platform: typeof PLATFORMS[0]; activeWidget: typeof WIDGETS[0] | null }) {
  const [copied, setCopied] = useState(false);
  const embedCode = activeWidget
    ? `<iframe src="${activeWidget.embedUrl}?city=chicago&theme=dark" width="100%" height="480" frameborder="0" allow="camera" loading="lazy"></iframe>`
    : '';

  const copyCode = () => {
    if (embedCode) {
      navigator.clipboard.writeText(embedCode).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  return (
    <div style={{ background: platform.bg, border: `1px solid ${platform.accent}25`, borderRadius: 8, overflow: 'hidden' }}>
      {/* Mock browser chrome */}
      <div style={{ background: 'rgba(0,0,0,0.4)', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${platform.accent}15` }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {['#e05252', '#c9a84c', '#2d7a47'].map((c, i) => (
            <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: c, opacity: 0.6 }} />
          ))}
        </div>
        <div style={{ flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: 3, padding: '3px 8px' }}>
          <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)', letterSpacing: '.04em' }}>{platform.mockUrl}</p>
        </div>
      </div>

      {/* Mock page content */}
      <div style={{ padding: '16px' }}>
        {/* Mock header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 10, borderBottom: `1px solid ${platform.accent}15` }}>
          <span style={{ fontSize: 16 }}>{platform.icon}</span>
          <div>
            <p style={{ fontFamily: 'monospace', fontSize: 10, color: platform.accent, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 700 }}>{platform.mockTitle}</p>
            <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)' }}>{platform.desc}</p>
          </div>
        </div>

        {/* Mock page body with widget embed zone */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          {/* Mock text content */}
          <div>
            {[80, 100, 60, 90, 70].map((w, i) => (
              <div key={i} style={{ height: 6, background: 'rgba(212,197,169,0.08)', borderRadius: 2, marginBottom: 5, width: `${w}%` }} />
            ))}
          </div>
          {/* Widget embed zone */}
          <div style={{ background: activeWidget ? `${activeWidget.color}08` : 'rgba(255,255,255,0.02)', border: `1px dashed ${activeWidget ? activeWidget.color + '40' : 'rgba(201,168,76,0.15)'}`, borderRadius: 6, padding: '12px', minHeight: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            {activeWidget ? (
              <>
                <span style={{ fontSize: 20, color: activeWidget.color }}>{activeWidget.icon}</span>
                <p style={{ fontFamily: 'monospace', fontSize: 8, color: activeWidget.color, letterSpacing: '.06em', textTransform: 'uppercase', textAlign: 'center' }}>{activeWidget.label}</p>
                <p style={{ fontFamily: 'monospace', fontSize: 6, color: 'rgba(212,197,169,0.3)', textAlign: 'center' }}>Embedded Widget</p>
              </>
            ) : (
              <>
                <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.25)', textAlign: 'center' }}>Select a widget</p>
                <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.15)', textAlign: 'center' }}>to preview embed</p>
              </>
            )}
          </div>
        </div>

        {/* Suggested widgets for this platform */}
        <div style={{ borderTop: `1px solid ${platform.accent}10`, paddingTop: 8 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 7, color: `${platform.accent}60`, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 5 }}>Suggested for this platform:</p>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {platform.suggestedWidgets.map(wid => {
              const w = WIDGETS.find(x => x.id === wid);
              if (!w) return null;
              return (
                <span key={wid} style={{ fontFamily: 'monospace', fontSize: 6, color: w.color, background: `${w.color}10`, border: `1px solid ${w.color}20`, padding: '2px 6px', borderRadius: 3 }}>
                  {w.icon} {w.label}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* Embed code */}
      {activeWidget && (
        <div style={{ borderTop: `1px solid ${platform.accent}15`, padding: '10px 14px', background: 'rgba(0,0,0,0.3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.4)', letterSpacing: '.1em', textTransform: 'uppercase' }}>Embed Code</p>
            <button onClick={copyCode} style={{ fontFamily: 'monospace', fontSize: 7, color: copied ? '#2d7a47' : 'rgba(201,168,76,0.5)', background: 'transparent', border: `1px solid ${copied ? 'rgba(45,122,71,0.3)' : 'rgba(201,168,76,0.2)'}`, borderRadius: 3, padding: '2px 8px', cursor: 'pointer', letterSpacing: '.06em' }}>
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <code style={{ display: 'block', fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', lineHeight: 1.5, wordBreak: 'break-all' }}>{embedCode}</code>
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function WidgetTestHarness() {
  const [activeWidget, setActiveWidget] = useState<string>('mrg');
  const [activePlatform, setActivePlatform] = useState<string>('masjid');

  const widget = WIDGETS.find(w => w.id === activeWidget) || null;
  const platform = PLATFORMS.find(p => p.id === activePlatform) || PLATFORMS[0];

  return (
    <section style={{ background: C.bg, borderTop: '1px solid rgba(201,168,76,0.08)', padding: '60px 0' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: C.goldText, marginBottom: 8 }}>
            ◈ WIDGET TEST HARNESS · PLATFORM EMBED PREVIEW
          </p>
          <h2 style={{ fontFamily: 'monospace', fontSize: 'clamp(14px, 3vw, 22px)', color: C.cream, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Widget Embed Test Harness
          </h2>
          <p style={{ color: C.creamFaint, fontSize: 12, lineHeight: 1.7, maxWidth: 600 }}>
            Preview each Haal Project widget embedded across 5 platform templates. Select a widget and a platform to see how it would appear and generate the embed code.
          </p>
        </div>

        {/* Platform selector */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, flexWrap: 'wrap' }}>
          {PLATFORMS.map(p => (
            <button
              key={p.id}
              onClick={() => setActivePlatform(p.id)}
              style={{ padding: '8px 14px', background: activePlatform === p.id ? `${p.color}15` : 'rgba(255,255,255,0.02)', border: `1px solid ${activePlatform === p.id ? p.color + '40' : 'rgba(201,168,76,0.1)'}`, borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <span style={{ fontSize: 12 }}>{p.icon}</span>
              <span style={{ fontFamily: 'monospace', fontSize: 8, color: activePlatform === p.id ? p.color : C.creamFaint, letterSpacing: '.06em', textTransform: 'uppercase' }}>{p.label}</span>
            </button>
          ))}
        </div>

        {/* Main grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20 }}>
          {/* Widget selector */}
          <div>
            <p style={{ fontFamily: 'monospace', fontSize: 8, color: C.goldText, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 10 }}>Select Widget</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {WIDGETS.map(w => (
                <WidgetCard key={w.id} widget={w} selected={activeWidget === w.id} onClick={() => setActiveWidget(w.id)} />
              ))}
            </div>
          </div>

          {/* Platform preview */}
          <div>
            <p style={{ fontFamily: 'monospace', fontSize: 8, color: C.goldText, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 10 }}>
              Platform Preview · {platform.label}
            </p>
            <PlatformPreview platform={platform} activeWidget={widget} />

            {/* All platforms quick view */}
            <div style={{ marginTop: 16 }}>
              <p style={{ fontFamily: 'monospace', fontSize: 7, color: C.goldText, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>All Platform Templates</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
                {PLATFORMS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setActivePlatform(p.id)}
                    style={{ padding: '8px 6px', background: activePlatform === p.id ? `${p.color}12` : 'rgba(255,255,255,0.02)', border: `1px solid ${activePlatform === p.id ? p.color + '35' : 'rgba(201,168,76,0.08)'}`, borderRadius: 5, cursor: 'pointer', textAlign: 'center' }}
                  >
                    <span style={{ display: 'block', fontSize: 14, marginBottom: 3 }}>{p.icon}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 6, color: activePlatform === p.id ? p.color : 'rgba(212,197,169,0.3)', letterSpacing: '.04em', textTransform: 'uppercase' }}>{p.label.split(' ')[0]}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
